// client/src/components/pages/LexiStar/Replay/replayPaths.ts
//
// Addresses of the Replay page (ST-00318), kept apart from the page so other pages (Live View,
// the Geofences page's old Trips links) can link to it without importing it.
//
//   /lexistar/replay                              the track replay (Trips, without Replay's permission)
//   /lexistar/replay?asset_key=reg:…&from=…&to=…  one truck over a window: a trip clicked on Trips
//   /lexistar/replay?tab=trips[&vehicle=…]        the Trips tab: every GPS trip of a day (ST-00159)
//   /lexistar/replay?tab=idle[&from=…&to=…]       the Idling heatmap (M4-D)
//
// The Trips tab was the Geofences page's (/geofencing?tab=trips) until ST-00318; that address
// forwards here with its parameters (Geofencing/index.tsx).
import { assetKeyOf } from './replayLogic';

export const REPLAY_PATH = '/lexistar/replay';

/** The page permission of each tab: Replay and the heatmap need Replay's, Trips the GPS trips'. */
export const REPLAY_PERMISSION = 'lexistar.replay.view';
/** GET /api/vehicle-locations/trips (requireGeofenceRead): what the Geofences Trips tab needed. */
export const TRIPS_PERMISSION = 'geofencing.view';

/** `?tab=` of the Trips tab and of the Idling heatmap (the track replay has none). */
export const TRIPS_TAB = 'trips';
export const IDLE_TAB = 'idle';

/**
 * The Trips tab's own query parameters (ST-00159), so a link reproduces the view: `vehicle`
 * (repeated: the vehicles to load; none = all), `from` / `to` (YYYY-MM-DD; today when absent),
 * `trip` (the trip selected), `hide` (repeated: trips switched off) and `hideVehicle`
 * (repeated: vehicles switched off).
 */
export const TRIPS_QUERY_KEYS = ['vehicle', 'from', 'to', 'trip', 'hide', 'hideVehicle'] as const;

/**
 * The Trips tab's address: on a vehicle (`{ vehicle }`), or carrying the Trips parameters of
 * another address (the old /geofencing?tab=trips… links; anything else in them is dropped).
 */
export function tripsTabPath(source?: { vehicle?: string | null } | URLSearchParams): string {
  const next = new URLSearchParams();
  next.set('tab', TRIPS_TAB);
  if (source instanceof URLSearchParams) {
    TRIPS_QUERY_KEYS.forEach((key) => source.getAll(key).forEach((v) => next.append(key, v)));
  } else if (source?.vehicle) {
    next.set('vehicle', source.vehicle);
  }
  return `${REPLAY_PATH}?${next}`;
}

/**
 * A GPS trip of the Trips tab replayed by Replay: the truck by its registration (the same
 * `reg:<PLATE>` key, capitals without spaces, that Replay's own form uses) from the trip's
 * start to its end.
 */
export function tripReplayPath(trip: { regNo: string; startTs: string; endTs: string }): string {
  const from = Date.parse(trip.startTs);
  const to = Date.parse(trip.endTs);
  const next = new URLSearchParams();
  next.set('asset_key', assetKeyOf({ registration: trip.regNo }));
  next.set('from', new Date(from).toISOString());
  // Replay needs a window: a trip that starts and ends on one fix gets a second.
  next.set('to', new Date(Math.max(to, from + 1000)).toISOString());
  return `${REPLAY_PATH}?${next}`;
}

/** What the Trips tab hands the replay it opens: the way back to the tab as it was. */
export interface ReplayNavState {
  tripsBack?: string;
}

/** The Trips tab address a replay was opened from (navigation state), else null. */
export function tripsBackOf(state: unknown): string | null {
  const back = (state as ReplayNavState | null | undefined)?.tripsBack;
  return typeof back === 'string' && back.startsWith(`${REPLAY_PATH}?`) ? back : null;
}
